/**
 * i18n — language state, translation lookup, and number/date localization.
 *
 * Language now lives in the URL (/fa/... or /en/...) rather than localStorage.
 * Django's LocalePrefixMiddleware already resolved it server-side and stamped
 * it on <html lang> plus window.NOVA_BOOT, so the client just reads it.
 *
 * Digit localization is applied at the RENDER boundary, never to stored data.
 * Anything numeric that reaches the DOM goes through `num()`; anything a user
 * types goes back through `toLatinDigits()` before it is sent to the API. That
 * split is what stops Persian digits leaking into query strings and IDs.
 */

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const ARABIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

const boot = window.NOVA_BOOT || {};

const state = {
    lang: boot.lang === 'en' ? 'en' : 'fa',
    dir: boot.dir === 'ltr' ? 'ltr' : 'rtl',
    catalog: {},
};

export const getLang = () => state.lang;
export const getDir = () => state.dir;
export const isRTL = () => state.dir === 'rtl';
export const isFa = () => state.lang === 'fa';

/** Prefix a site-relative path with the active locale. */
export function localePath(path = '/') {
    const clean = String(path || '/').replace(/^\/(fa|en)(?=\/|$)/, '') || '/';
    const withSlash = clean.startsWith('/') ? clean : `/${clean}`;
    return `/${state.lang}${withSlash === '/' ? '/' : withSlash}`;
}

/** Strip the locale prefix, giving the route the SPA router should match. */
export function stripLocale(pathname) {
    return String(pathname || '/').replace(/^\/(fa|en)(?=\/|$)/, '') || '/';
}

/** Look a key up in the server-provided catalogue, falling back to the key. */
export function gettext(msgid) {
    if (typeof window.gettext === 'function') {
        return window.gettext(msgid);
    }
    return state.catalog[msgid] || msgid;
}

export async function loadCatalog() {
    try {
        const res = await fetch(`/dashboard/api/i18n/?lang=${state.lang}`, {
            headers: {'X-Requested-With': 'fetch'},
        });
        if (res.ok) {
            const data = await res.json();
            state.catalog = data.catalog || {};
        }
    } catch {
        state.catalog = {};
    }
}

/* ------------------------------------------------------------------ *
 * Digits
 * ------------------------------------------------------------------ */

/** ASCII digits -> Persian digits. */
export function toPersianDigits(value) {
    if (value === null || value === undefined) return '';
    return String(value).replace(/\d/g, (d) => PERSIAN_DIGITS[+d]);
}

/** Persian or Arabic-Indic digits -> ASCII. Use before sending anything out. */
export function toLatinDigits(value) {
    if (value === null || value === undefined) return '';
    let out = String(value);
    PERSIAN_DIGITS.forEach((d, i) => {
        out = out.split(d).join(String(i));
    });
    ARABIC_DIGITS.forEach((d, i) => {
        out = out.split(d).join(String(i));
    });
    // Persian thousands separator and decimal mark.
    return out.replace(/٫/g, '.').replace(/٬/g, ',');
}

/**
 * Render a number in the active locale's script.
 * Pass `{ group: true }` for thousands separators.
 */
export function num(value, {group = false, fallback = ''} = {}) {
    if (value === null || value === undefined || value === '') return fallback;

    let text = String(value);

    if (group) {
        const asNumber = Number(toLatinDigits(text));
        if (!Number.isNaN(asNumber)) {
            text = asNumber.toLocaleString(state.lang === 'en' ? 'en-US' : 'en-US');
        }
    }

    return state.lang === 'fa' ? toPersianDigits(text) : toLatinDigits(text);
}

/** Convenience for counts that read better with a grouping separator. */
export const count = (value) => num(value, {group: true, fallback: num(0)});

/* ------------------------------------------------------------------ *
 * Dates
 * ------------------------------------------------------------------ */

/**
 * Format an ISO timestamp for display.
 *
 * The Persian branch uses Intl with the `persian` calendar, which every
 * evergreen browser ships. That removes the old jalaali-js CDN script
 * entirely: one less thing the offline VPS cannot reach, and one less
 * dependency to bundle.
 */
export function formatDate(value, {withTime = false} = {}) {
    if (!value) return '';

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const options = {year: 'numeric', month: 'long', day: 'numeric'};
    if (withTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
    }

    try {
        const locale = state.lang === 'fa' ? 'fa-IR-u-ca-persian-nu-arabext' : 'en-GB';
        return new Intl.DateTimeFormat(locale, options).format(date);
    } catch {
        // Very old browser without the Persian calendar: degrade to ISO.
        return num(date.toISOString().slice(0, 10));
    }
}

/** Short numeric date, e.g. ۱۴۰۳/۰۵/۱۲ or 2024/08/02. */
export function formatDateShort(value) {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return num(String(value));

    try {
        const locale = state.lang === 'fa' ? 'fa-IR-u-ca-persian-nu-arabext' : 'en-GB';
        return new Intl.DateTimeFormat(locale, {
            year: 'numeric', month: '2-digit', day: '2-digit',
        }).format(date).replace(/-/g, '/');
    } catch {
        return num(date.toISOString().slice(0, 10).replace(/-/g, '/'));
    }
}

/* ------------------------------------------------------------------ *
 * Validation messages — localized so the browser's untranslated native
 * bubble never appears. Consumed by ui/forms.js.
 * ------------------------------------------------------------------ */

export const messages = {
    required: () => gettext('This field is required.'),
    email: () => gettext('Enter a valid email address.'),
    phone: () => gettext('Enter a valid phone number.'),
    minLength: (n) => gettext('Enter at least {n} characters.').replace('{n}', num(n)),
    maxLength: (n) => gettext('No more than {n} characters.').replace('{n}', num(n)),
    numeric: () => gettext('Numbers only.'),
    url: () => gettext('Enter a valid URL.'),
    generic: () => gettext('This value is not valid.'),
    networkError: () => gettext('Could not reach the server. Please try again.'),
    submitSuccess: () => gettext('Your request has been submitted. We will get back to you shortly.'),
};

/**
 * Apply the right `dir` for a field based on what it holds. Emails, URLs,
 * phone numbers and pure numbers are always LTR even on the Persian site;
 * free text follows its own content.
 */
export function fieldDirection(type, name = '') {
    const ltrTypes = ['email', 'url', 'tel', 'number'];
    const ltrNames = ['email', 'phone', 'mobile', 'url', 'website', 'link'];
    if (ltrTypes.includes(type)) return 'ltr';
    if (ltrNames.some((n) => name.toLowerCase().includes(n))) return 'ltr';
    return 'auto';
}
